import React, { useState } from 'react';
import CalcShell from '../CalcShell';

const LENGTH_UNITS = ['meters','kilometers','centimeters','millimeters','micrometers','miles','yards','feet','inches'];

function UnitSelect({ value, onChange, testid }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
      className="px-3 py-2 rounded border border-slate-300 bg-white text-sm">
      {LENGTH_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
    </select>
  );
}
function CalcButtons({ onCalc, onClear, testId }) {
  return (
    <div className="flex gap-2 mt-3">
      <button onClick={onCalc} data-testid={`${testId}-calc`}
        className="inline-flex items-center gap-2 px-5 py-2 rounded bg-[#4c7b25] hover:bg-[#3d621d] text-white font-semibold text-sm shadow-sm">
        <span className="inline-block w-0 h-0 border-l-[7px] border-l-white border-y-[5px] border-y-transparent"></span> Calculate
      </button>
      <button onClick={onClear} data-testid={`${testId}-clear`}
        className="px-5 py-2 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium text-sm">Clear</button>
    </div>
  );
}
function InputRow({ label, value, onChange, unit, onUnitChange, testid, unitTestid }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[140px_1fr_150px] items-center gap-2 mb-2">
      <label className="text-sm text-slate-700">{label}</label>
      <input type="number" step="any" value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="px-3 py-2 rounded border border-slate-300 bg-white w-full" />
      {onUnitChange && <UnitSelect value={unit} onChange={onUnitChange} testid={unitTestid} />}
    </div>
  );
}
function ResultBlock({ show, lines, finalValue, finalUnit }) {
  if (!show) return null;
  return (
    <div className="mt-3">
      <div className="bg-[#4c7b25] text-white px-4 py-1.5 text-lg font-bold rounded-t">Result</div>
      <div className="bg-white border border-slate-200 rounded-b p-4 font-serif">
        <div className="text-slate-800 text-[15px] leading-8">
          {lines.map((ln, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="min-w-[110px]">{i === 0 ? 'Surface Area =' : '='}</span>
              <span dangerouslySetInnerHTML={{ __html: ln }} />
            </div>
          ))}
          <div className="flex items-start gap-2 mt-1">
            <span className="min-w-[110px]">=</span>
            <span className="font-bold text-[#4c7b25] text-lg">{finalValue} <span className="text-base">{finalUnit}<sup>2</sup></span></span>
          </div>
        </div>
      </div>
    </div>
  );
}
function ShapeCard({ title, children }) {
  return (
    <section className="mt-6 first:mt-0">
      <h3 className="text-xl font-bold text-[#1e3a8a] mb-2">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-4 items-start">
        <div className="bg-slate-100 rounded-md p-4">{children[0]}</div>
        <div className="flex items-center justify-center p-3">{children[1]}</div>
      </div>
      {children[2]}
    </section>
  );
}

const G = '#4a7c1f';
const dashed = { stroke: G, strokeDasharray: '3 3', fill: 'none' };
const solid = { stroke: '#0f172a', fill: 'none', strokeWidth: 1.5 };
const lbl = { fill: G, fontSize: 12, fontStyle: 'italic', fontFamily: 'serif' };

function SphereD() {
  return (
    <svg viewBox="0 0 200 200" className="w-full max-w-[220px]">
      <circle cx="100" cy="100" r="75" {...solid} />
      <ellipse cx="100" cy="100" rx="75" ry="18" {...dashed} />
      <line x1="100" y1="100" x2="170" y2="105" {...dashed} />
      <circle cx="100" cy="100" r="2" fill="#0f172a" />
      <text x="140" y="97" style={lbl}>r</text>
    </svg>
  );
}
function ConeD() {
  return (
    <svg viewBox="0 0 200 220" className="w-full max-w-[220px]">
      <path d="M100,20 L165,170 L35,170" {...solid} />
      <ellipse cx="100" cy="170" rx="65" ry="15" {...solid} />
      <line x1="100" y1="20" x2="100" y2="170" {...dashed} />
      <line x1="100" y1="170" x2="165" y2="175" {...dashed} />
      <text x="105" y="100" style={lbl}>h</text>
      <text x="140" y="165" style={lbl}>r</text>
    </svg>
  );
}
function CubeD() {
  return (
    <svg viewBox="0 0 200 200" className="w-full max-w-[220px]">
      <rect x="40" y="60" width="100" height="100" {...solid} />
      <line x1="40" y1="60" x2="80" y2="30" {...solid} />
      <line x1="140" y1="60" x2="180" y2="30" {...solid} />
      <line x1="140" y1="160" x2="180" y2="130" {...solid} />
      <line x1="80" y1="30" x2="180" y2="30" {...solid} />
      <line x1="180" y1="30" x2="180" y2="130" {...solid} />
      <line x1="40" y1="160" x2="80" y2="130" {...dashed} />
      <line x1="80" y1="30" x2="80" y2="130" {...dashed} />
      <line x1="80" y1="130" x2="180" y2="130" {...dashed} />
      <text x="140" y="175" style={lbl}>a</text>
    </svg>
  );
}
function CylD() {
  return (
    <svg viewBox="0 0 200 220" className="w-full max-w-[220px]">
      <line x1="55" y1="40" x2="55" y2="180" {...solid} />
      <line x1="145" y1="40" x2="145" y2="180" {...solid} />
      <ellipse cx="100" cy="40" rx="45" ry="12" {...solid} />
      <path d="M55,180 A45,12 0 0 0 145,180" {...solid} />
      <path d="M55,180 A45,12 0 0 1 145,180" {...dashed} />
      <line x1="100" y1="40" x2="100" y2="180" {...dashed} />
      <line x1="100" y1="180" x2="145" y2="182" {...dashed} />
      <text x="105" y="115" style={lbl}>h</text>
      <text x="122" y="178" style={lbl}>r</text>
    </svg>
  );
}
function RectTankD() {
  return (
    <svg viewBox="0 0 240 200" className="w-full max-w-[240px]">
      <rect x="30" y="60" width="140" height="100" {...solid} />
      <line x1="30" y1="60" x2="70" y2="30" {...solid} />
      <line x1="170" y1="60" x2="210" y2="30" {...solid} />
      <line x1="170" y1="160" x2="210" y2="130" {...solid} />
      <line x1="70" y1="30" x2="210" y2="30" {...solid} />
      <line x1="210" y1="30" x2="210" y2="130" {...solid} />
      <line x1="30" y1="160" x2="70" y2="130" {...dashed} />
      <line x1="70" y1="30" x2="70" y2="130" {...dashed} />
      <text x="95" y="175" style={lbl}>l</text>
      <text x="180" y="175" style={lbl}>w</text>
      <text x="15" y="115" style={lbl}>h</text>
    </svg>
  );
}
function PyrD() {
  return (
    <svg viewBox="0 0 220 200" className="w-full max-w-[220px]">
      <path d="M110,20 L200,150 L20,150 Z" {...solid} />
      <path d="M20,150 L60,180 L160,180 L200,150" {...solid} />
      <line x1="60" y1="180" x2="160" y2="180" {...dashed} />
      <line x1="110" y1="20" x2="110" y2="165" {...dashed} />
      <text x="115" y="100" style={lbl}>h</text>
      <text x="105" y="195" style={lbl}>a</text>
    </svg>
  );
}

/* Sections */
function BallS() {
  const [r, setR] = useState(''), [unit, setUnit] = useState('meters'), [res, setRes] = useState(null);
  const calc = () => { const R = parseFloat(r); if (!R) return; setRes({ v: 4 * Math.PI * R * R, R }); };
  return (
    <ShapeCard title="Ball Surface Area">
      <div>
        <InputRow label="Radius (r)" value={r} onChange={setR} unit={unit} onUnitChange={setUnit} testid="ball-r" unitTestid="ball-unit" />
        <CalcButtons testId="ball-sa" onCalc={calc} onClear={() => { setR(''); setRes(null); }} />
      </div>
      <SphereD />
      <ResultBlock show={!!res} finalUnit={unit} finalValue={res && res.v.toFixed(11)} lines={res ? [`4πr<sup>2</sup>`, `4 × π × ${res.R}<sup>2</sup>`] : []} />
    </ShapeCard>
  );
}
function ConeS() {
  const [r, setR] = useState(''), [h, setH] = useState(''), [unit, setUnit] = useState('meters'), [res, setRes] = useState(null);
  const calc = () => { const R = parseFloat(r), H = parseFloat(h); if (!R || !H) return; const slant = Math.sqrt(R*R + H*H); const lateral = Math.PI * R * slant; const base = Math.PI * R * R; setRes({ v: lateral + base, R, H, slant }); };
  return (
    <ShapeCard title="Cone Surface Area">
      <div>
        <InputRow label="Base Radius (r)" value={r} onChange={setR} unit={unit} onUnitChange={setUnit} testid="cone-r" unitTestid="cone-r-unit" />
        <InputRow label="Height (h)" value={h} onChange={setH} unit={unit} onUnitChange={setUnit} testid="cone-h" unitTestid="cone-h-unit" />
        <CalcButtons testId="cone-sa" onCalc={calc} onClear={() => { setR(''); setH(''); setRes(null); }} />
      </div>
      <ConeD />
      <ResultBlock show={!!res} finalUnit={unit} finalValue={res && res.v.toFixed(11)} lines={res ? [`πr(r + √(h<sup>2</sup> + r<sup>2</sup>))`, `π × ${res.R} × (${res.R} + √(${res.H}<sup>2</sup> + ${res.R}<sup>2</sup>))`, `π × ${res.R} × (${res.R} + ${res.slant.toFixed(4)})`] : []} />
    </ShapeCard>
  );
}
function CubeS() {
  const [a, setA] = useState(''), [unit, setUnit] = useState('meters'), [res, setRes] = useState(null);
  const calc = () => { const A = parseFloat(a); if (!A) return; setRes({ v: 6 * A * A, A }); };
  return (
    <ShapeCard title="Cube Surface Area">
      <div>
        <InputRow label="Edge Length (a)" value={a} onChange={setA} unit={unit} onUnitChange={setUnit} testid="cube-a" unitTestid="cube-unit" />
        <CalcButtons testId="cube-sa" onCalc={calc} onClear={() => { setA(''); setRes(null); }} />
      </div>
      <CubeD />
      <ResultBlock show={!!res} finalUnit={unit} finalValue={res && res.v.toString()} lines={res ? [`6a<sup>2</sup>`, `6 × ${res.A}<sup>2</sup>`] : []} />
    </ShapeCard>
  );
}
function CylS() {
  const [r, setR] = useState(''), [h, setH] = useState(''), [unit, setUnit] = useState('meters'), [res, setRes] = useState(null);
  const calc = () => { const R = parseFloat(r), H = parseFloat(h); if (!R || !H) return; setRes({ v: 2 * Math.PI * R * (R + H), R, H }); };
  return (
    <ShapeCard title="Cylindrical Tank Surface Area">
      <div>
        <InputRow label="Base Radius (r)" value={r} onChange={setR} unit={unit} onUnitChange={setUnit} testid="cyl-r" unitTestid="cyl-r-unit" />
        <InputRow label="Height (h)" value={h} onChange={setH} unit={unit} onUnitChange={setUnit} testid="cyl-h" unitTestid="cyl-h-unit" />
        <CalcButtons testId="cyl-sa" onCalc={calc} onClear={() => { setR(''); setH(''); setRes(null); }} />
      </div>
      <CylD />
      <ResultBlock show={!!res} finalUnit={unit} finalValue={res && res.v.toFixed(11)} lines={res ? [`2πr(r + h)`, `2 × π × ${res.R} × (${res.R} + ${res.H})`] : []} />
    </ShapeCard>
  );
}
function RectS() {
  const [l, setL] = useState(''), [w, setW] = useState(''), [h, setH] = useState(''), [unit, setUnit] = useState('meters'), [res, setRes] = useState(null);
  const calc = () => { const L = parseFloat(l), W = parseFloat(w), H = parseFloat(h); if (!L || !W || !H) return; setRes({ v: 2 * (L * W + W * H + L * H), L, W, H }); };
  return (
    <ShapeCard title="Rectangular Tank Surface Area">
      <div>
        <InputRow label="Length (l)" value={l} onChange={setL} unit={unit} onUnitChange={setUnit} testid="rect-l" unitTestid="rect-l-unit" />
        <InputRow label="Width (w)" value={w} onChange={setW} unit={unit} onUnitChange={setUnit} testid="rect-w" unitTestid="rect-w-unit" />
        <InputRow label="Height (h)" value={h} onChange={setH} unit={unit} onUnitChange={setUnit} testid="rect-h" unitTestid="rect-h-unit" />
        <CalcButtons testId="rect-sa" onCalc={calc} onClear={() => { setL(''); setW(''); setH(''); setRes(null); }} />
      </div>
      <RectTankD />
      <ResultBlock show={!!res} finalUnit={unit} finalValue={res && res.v.toString()} lines={res ? [`2(lw + wh + lh)`, `2 × (${res.L}×${res.W} + ${res.W}×${res.H} + ${res.L}×${res.H})`] : []} />
    </ShapeCard>
  );
}
function PyrS() {
  const [a, setA] = useState(''), [h, setH] = useState(''), [unit, setUnit] = useState('meters'), [res, setRes] = useState(null);
  const calc = () => { const A = parseFloat(a), H = parseFloat(h); if (!A || !H) return; const slant = Math.sqrt((A/2)*(A/2) + H*H); setRes({ v: A*A + 2*A*slant, A, H, slant }); };
  return (
    <ShapeCard title="Square Pyramid Surface Area">
      <div>
        <InputRow label="Base Edge (a)" value={a} onChange={setA} unit={unit} onUnitChange={setUnit} testid="pyr-a" unitTestid="pyr-a-unit" />
        <InputRow label="Height (h)" value={h} onChange={setH} unit={unit} onUnitChange={setUnit} testid="pyr-h" unitTestid="pyr-h-unit" />
        <CalcButtons testId="pyr-sa" onCalc={calc} onClear={() => { setA(''); setH(''); setRes(null); }} />
      </div>
      <PyrD />
      <ResultBlock show={!!res} finalUnit={unit} finalValue={res && res.v.toFixed(11)} lines={res ? [`a<sup>2</sup> + 2a√((a/2)<sup>2</sup> + h<sup>2</sup>)`, `${res.A}<sup>2</sup> + 2×${res.A}×√((${res.A}/2)<sup>2</sup> + ${res.H}<sup>2</sup>)`, `${res.A}<sup>2</sup> + 2×${res.A}×${res.slant.toFixed(4)}`] : []} />
    </ShapeCard>
  );
}

export default function SurfaceAreaCalc() {
  return (
    <CalcShell look="cnet"
      title="Surface Area Calculator"
      subtitle="Use the calculators below to calculate the surface area of several common shapes."
    >
      <BallS />
      <ConeS />
      <CubeS />
      <CylS />
      <RectS />
      <PyrS />
    </CalcShell>
  );
}
